import { useState } from 'react';
import { useSEO } from '../../utils/seo';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import HowItWorks from './components/HowItWorks';
import ReviewDemo from './components/ReviewDemo';
import WhyImportant from './components/WhyImportant';
import Pricing from './components/Pricing';
import FAQ from './components/FAQ';
import FinalCTA from './components/FinalCTA';
import Footer from './components/Footer';

export default function HomePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://boostio.fi';

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Miten Boostio toimii?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Boostio lähettää automaattisesti palautekyselyt asiakkaillesi sähköpostilla. Tyytyväiset asiakkaat ohjataan jättämään arvostelu Google-profiiliisi, kun taas negatiivinen palaute ohjataan suoraan sinulle käsiteltäväksi.'
        }
      },
      {
        '@type': 'Question',
        name: 'Kuinka nopeasti näen tuloksia?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Useimmat asiakkaamme näkevät ensimmäiset uudet arvostelut jo ensimmäisen viikon aikana. Keskimäärin asiakkaamme saavat 15-30 uutta arvostelua ensimmäisen kuukauden aikana.'
        }
      },
      {
        '@type': 'Question',
        name: 'Onko palvelu Googlen sääntöjen mukainen?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Kyllä, Boostio noudattaa täysin Googlen ohjeita. Emme koskaan maksa arvosteluista tai kannusta vain positiivisiin arvosteluihin. Lähetämme aidon palautekyselyn kaikille asiakkaille.'
        }
      },
      {
        '@type': 'Question',
        name: 'Mitä tapahtuu negatiiviselle palautteelle?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Negatiivinen palaute ohjataan suoraan sinulle käsiteltäväksi, ei julkisesti Googleen. Näin voit ratkaista ongelmat ennen kuin ne vaikuttavat julkiseen arvosteluprofiiliisi.'
        }
      },
      {
        '@type': 'Question',
        name: 'Voinko perua milloin tahansa?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Kaikki paketit sisältävät 14 päivän ilmaisen kokeilun, jonka jälkeen palvelu on 12 kk määräajalla.'
        }
      }
    ]
  };

  const businessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Boostio',
    image: `${siteUrl}/vite.svg`,
    '@id': siteUrl,
    url: siteUrl,
    telephone: '+358443231550',
    email: 'info@boostio.fi',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Tarjantie 17',
      addressLocality: 'Lahti',
      postalCode: '15900',
      addressCountry: 'FI'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 60.9827,
      longitude: 25.6612
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday'
      ],
      opens: '09:00',
      closes: '17:00'
    },
    sameAs: [],
    priceRange: '€€',
    description: 'Boostio auttaa yrityksiä keräämään enemmän aitoja Google-arvosteluja automaattisesti. Lähetä asiakkaillesi helppo palautekysely sähköpostilla ja ohjaa tyytyväiset asiakkaat jättämään Google-arvostelun.'
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Hanki enemmän Google-arvosteluja – automaattisesti',
    description: 'Boostio auttaa yrityksiä keräämään enemmän aitoja Google-arvosteluja automaattisesti. Lähetämme asiakkaillesi helpon palautekyselyn sähköpostilla ja tyytyväiset asiakkaat ohjataan jättämään Google-arvostelu ilman manuaalista työtä. Paranna yrityksen näkyvyyttä ja luotettavuutta.',
    url: siteUrl,
    inLanguage: 'fi',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Boostio',
      url: siteUrl
    }
  };

  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [webPageSchema, businessSchema, faqSchema]
  };

  useSEO({
    title: 'Hanki enemmän Google-arvosteluja – automaattisesti',
    description: 'Boostio auttaa yrityksiä keräämään enemmän aitoja Google-arvosteluja automaattisesti. Lähetämme asiakkaillesi helpon palautekyselyn sähköpostilla ja tyytyväiset asiakkaat ohjataan jättämään Google-arvostelu ilman manuaalista työtä. Paranna yrityksen näkyvyyttä ja luotettavuutta.',
    keywords: 'Google-arvostelut, asiakaspalaute, arvostelut automaattisesti, yritysarvostelut, Google My Business',
    canonical: siteUrl,
    schema: combinedSchema
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Hero />
      <HowItWorks />
      <ReviewDemo />
      <WhyImportant />
      <Pricing />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}
